import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Package, Heart, RotateCcw, LifeBuoy, UserCircle, Truck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatUSD } from "@/lib/format";
import { ProductCard } from "@/components/ProductCard";
import { Footer } from "@/components/Footer";
import { BackButton } from "@/components/BackButton";

const TABS = [
  { key: "orders", label: "My Orders", icon: Package },
  { key: "wishlist", label: "Wishlist", icon: Heart },
  { key: "returns", label: "Returns & Refunds", icon: RotateCcw },
  { key: "support", label: "Service & Support", icon: LifeBuoy },
  { key: "profile", label: "Profile", icon: UserCircle },
];

const inputCls =
  "w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal";

const Badge = ({ children, tone = "sand" }) => (
  <span className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.15em] ${tone === "green" ? "bg-moss/10 text-charcoal" : "bg-sand text-ash"}`}>
    {children}
  </span>
);

export default function Account() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "orders");
  const [data, setData] = useState(null);
  const [retForm, setRetForm] = useState({ order_id: "", reason: "" });
  const [ticket, setTicket] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user === null) navigate("/auth");
  }, [user, navigate]);

  const load = () => api.get("/my/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  if (!user || !data) {
    return <main className="flex min-h-screen items-center justify-center" data-testid="account-loading">
      <p className="text-sm font-light text-ash">Opening your account...</p>
    </main>;
  }

  const paidOrders = data.orders.filter((o) => o.payment_status === "paid");

  const submitReturn = async () => {
    setBusy(true);
    try {
      await api.post("/my/returns", retForm);
      toast.success("Return request received — we'll email you the next steps.");
      setRetForm({ order_id: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const submitTicket = async () => {
    setBusy(true);
    try {
      await api.post("/my/support", ticket);
      toast.success("Support request sent — our team will reply by email.");
      setTicket({ subject: "", message: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <main data-testid="account-page" className="pt-24">
      <div className="mx-auto max-w-[1400px] px-6 py-16 sm:px-10">
        <BackButton />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-taupe">Members</p>
          <h1 className="font-serif text-4xl font-light tracking-tighter sm:text-5xl">
            Hello, {data.user.name?.split(" ")[0] || "friend"}.
          </h1>
          <p className="mt-4 text-sm font-light text-ash">
            Member pricing is on: ${data.member_discount.amount} off orders over ${data.member_discount.min_subtotal} — applied automatically at checkout.
          </p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2" data-testid="account-tabs">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`account-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t.key ? "border-charcoal bg-charcoal text-bone" : "border-charcoal/20 text-ash hover:text-charcoal"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-12">
          {tab === "orders" && (
            <div data-testid="account-orders">
              {data.orders.length === 0 ? (
                <p className="text-sm font-light text-ash">No orders yet — <Link to="/shop" className="underline underline-offset-4">start gathering</Link>.</p>
              ) : (
                <div className="space-y-6">
                  {data.orders.map((o) => (
                    <div key={o.order_id} data-testid={`order-card-${o.order_id}`} className="rounded-md border border-charcoal/10 bg-sand/40 p-6 sm:p-8">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="font-serif text-xl">{o.order_id}</p>
                        <div className="flex items-center gap-3">
                          <Badge tone={o.payment_status === "paid" ? "green" : "sand"}>{o.payment_status}</Badge>
                          <Badge>{o.fulfillment.replace(/_/g, " ")}</Badge>
                          <span className="text-sm text-ash">{formatUSD(o.amount)}</span>
                        </div>
                      </div>
                      <ul className="mt-4 space-y-1 text-sm font-light text-ash">
                        {o.items.map((it, i) => (
                          <li key={i}>{it.name}{it.variant ? ` — ${it.variant}` : ""} × {it.qty}</li>
                        ))}
                      </ul>
                      <Link to="/track" data-testid={`order-track-${o.order_id}`}
                        className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-taupe hover:text-charcoal">
                        <Truck size={13} /> Track this order
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "wishlist" && (
            <div data-testid="account-wishlist">
              {data.wishlist.length === 0 ? (
                <p className="text-sm font-light text-ash">Nothing saved yet — tap the heart on any piece you love.</p>
              ) : (
                <div className="grid grid-cols-2 gap-6 md:grid-cols-4 md:gap-10">
                  {data.wishlist.map((p) => <ProductCard key={p.slug} product={p} ratio="aspect-[4/5]" />)}
                </div>
              )}
            </div>
          )}

          {tab === "returns" && (
            <div data-testid="account-returns" className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              <div>
                <h2 className="font-serif text-2xl font-light">Request a return</h2>
                <p className="mt-3 text-sm font-light text-ash">Pick one of your paid orders — refunds are reviewed within two working days and paid back to your original payment method.</p>
                <div className="mt-6 space-y-4">
                  <select data-testid="return-order-select" value={retForm.order_id}
                    onChange={(e) => setRetForm((f) => ({ ...f, order_id: e.target.value }))} className={inputCls}>
                    <option value="">Choose an order…</option>
                    {paidOrders.map((o) => <option key={o.order_id} value={o.order_id}>{o.order_id} — {formatUSD(o.amount)}</option>)}
                  </select>
                  <textarea data-testid="return-reason" rows={4} placeholder="Why are you returning it?" value={retForm.reason}
                    onChange={(e) => setRetForm((f) => ({ ...f, reason: e.target.value }))} className={inputCls} />
                  <button data-testid="return-submit" onClick={submitReturn} disabled={busy || !retForm.order_id || !retForm.reason}
                    className="rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60">
                    Request return
                  </button>
                </div>
              </div>
              <div>
                <h2 className="font-serif text-2xl font-light">Your returns</h2>
                <div className="mt-6 space-y-4">
                  {data.returns.length === 0 && <p className="text-sm font-light text-ash">No returns requested.</p>}
                  {data.returns.map((r) => (
                    <div key={r.return_id} data-testid={`return-card-${r.return_id}`} className="rounded-md border border-charcoal/10 p-5">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{r.return_id}</p>
                        <Badge>{r.status}</Badge>
                      </div>
                      <p className="mt-2 text-sm font-light text-ash">Order {r.order_id} · refund {r.refund_status.replace(/_/g, " ")}</p>
                      <p className="mt-1 text-sm font-light text-ash">{r.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "support" && (
            <div data-testid="account-support" className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              <div>
                <h2 className="font-serif text-2xl font-light">Account support</h2>
                <p className="mt-3 text-sm font-light text-ash">Order help, delivery changes, product questions — we reply by email, usually same day.</p>
                <div className="mt-6 space-y-4">
                  <input data-testid="ticket-subject" placeholder="Subject" value={ticket.subject}
                    onChange={(e) => setTicket((f) => ({ ...f, subject: e.target.value }))} className={inputCls} />
                  <textarea data-testid="ticket-message" rows={5} placeholder="Tell us what you need" value={ticket.message}
                    onChange={(e) => setTicket((f) => ({ ...f, message: e.target.value }))} className={inputCls} />
                  <button data-testid="ticket-submit" onClick={submitTicket} disabled={busy || !ticket.subject || !ticket.message}
                    className="rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60">
                    Send to support
                  </button>
                </div>
              </div>
              <div>
                <h2 className="font-serif text-2xl font-light">Your requests</h2>
                <div className="mt-6 space-y-4">
                  {data.tickets.length === 0 && <p className="text-sm font-light text-ash">No support requests yet.</p>}
                  {data.tickets.map((t) => (
                    <div key={t.ticket_id} data-testid={`ticket-card-${t.ticket_id}`} className="rounded-md border border-charcoal/10 p-5">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{t.subject}</p>
                        <Badge>{t.status}</Badge>
                      </div>
                      <p className="mt-2 text-sm font-light text-ash">{t.ticket_id} · {t.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "profile" && (
            <div data-testid="account-profile" className="max-w-lg">
              <h2 className="font-serif text-2xl font-light">Profile</h2>
              <dl className="mt-6 space-y-4 text-sm">
                <div className="flex justify-between border-b border-charcoal/10 pb-3"><dt className="text-ash">Name</dt><dd data-testid="profile-name">{data.user.name}</dd></div>
                <div className="flex justify-between border-b border-charcoal/10 pb-3"><dt className="text-ash">Email</dt><dd data-testid="profile-email">{data.user.email || "—"}</dd></div>
                <div className="flex justify-between border-b border-charcoal/10 pb-3"><dt className="text-ash">Mobile</dt><dd data-testid="profile-mobile">{data.user.mobile || "—"}</dd></div>
              </dl>
              {data.user.default_shipping && (
                <div className="mt-8">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-ash">Saved delivery address</h3>
                  <p data-testid="profile-address" className="mt-3 text-sm font-light">
                    {data.user.default_shipping.address}, {data.user.default_shipping.city} {data.user.default_shipping.zip}
                  </p>
                  <p className="mt-2 text-xs font-light text-ash">This address prefills your checkout — update it by placing an order with new details.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
